import { useCallback } from "react";
import {
  trackEvent,
  trackBeginCheckout,
  trackPlanSelected,
  trackPurchase,
  trackUpsellPurchase,
  trackSignupComplete,
  trackViewPricing,
  trackChromeInstall,
} from "../helpers/analytics";
import { useAppSelector } from "../store/hooks";
import { selectUserProfile } from "../features/user/user.selector";
import { TBillingPeriod, TPricingPlan } from "../features/pricingPlans/pricingPlans.types";

export function useAnalytics() {
  const user = useAppSelector(selectUserProfile);

  return {
    trackEvent: useCallback(trackEvent, []),
    trackBeginCheckout: useCallback(
      ({
        planId,
        planName,
        planPrice,
        currency,
        planDuration,
      }: {
        planId: string;
        planName: string;
        planPrice: number;
        currency?: string;
        planDuration?: TBillingPeriod;
      }) => {
        if (user) {
          trackBeginCheckout({
            userId: user.id,
            email: user.email,
            name: user.fullName,
            planId,
            planName,
            planPrice,
            currency,
            planDuration,
          });
        }
      },
      [user?.id, user?.email, user?.fullName],
    ),
    trackPlanSelected: useCallback(
      ({
        planId,
        planName,
        planPrice,
        currency,
        planDuration,
      }: {
        planId: string;
        planName: string;
        planPrice: number;
        currency?: string;
        planDuration?: TBillingPeriod;
      }) => {
        if (user) {
          trackPlanSelected({
            userId: user.id,
            email: user.email,
            name: user.fullName,
            planId,
            planName,
            planPrice,
            currency,
            planDuration,
          });
        }
      },
      [user?.id, user?.email, user?.fullName],
    ),
    trackPurchase: useCallback(
      ({
        planId,
        planName,
        planPrice,
        currency,
        planDuration,
        transactionId,
      }: {
        planId: string;
        planName: string;
        planPrice: number;
        currency?: string;
        planDuration?: TBillingPeriod;
        transactionId?: string;
      }) => {
        if (user) {
          trackPurchase({
            userId: user.id,
            email: user.email,
            name: user.fullName,
            planId,
            planName,
            planPrice,
            currency,
            planDuration,
            transactionId,
          });
        }
      },
      [user?.id, user?.email, user?.fullName],
    ),
    trackUpsellPurchase: useCallback(
      ({
        planId,
        planName,
        planPrice,
        currency,
        planDuration,
        transactionId,
      }: {
        planId: string;
        planName: string;
        planPrice: number;
        currency?: string;
        planDuration?: TBillingPeriod;
        transactionId?: string;
      }) => {
        if (user) {
          trackUpsellPurchase({
            userId: user.id,
            email: user.email,
            name: user.fullName,
            planId,
            planName,
            planPrice,
            currency,
            planDuration,
            transactionId,
          });
        }
      },
      [user?.id, user?.email, user?.fullName],
    ),
    trackSignupComplete: useCallback(trackSignupComplete, []),
    trackViewPricing: useCallback(
      (plans: TPricingPlan[]) => {
        if (user) {
          trackViewPricing({
            userId: user.id,
            email: user.email,
            name: user.fullName,
            plans: plans.map(plan => ({
              id: plan.id,
              name: plan.name,
              price: plan.price,
              period: plan.period,
            })),
          });
        }
      },
      [user?.id, user?.email, user?.fullName],
    ),
    trackChromeInstall: useCallback(trackChromeInstall, []),
  };
}
